import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import {
  ClockIcon,
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  UserGroupIcon,    // 03 Jul 2026
} from '@heroicons/react/24/solid'

import { getTimeSince } from '@/utils/EtGetTimeSince'

// 03 Jul 2026
import EtUserSessionQNABoard from '../userSessionQNAResponses/EtUserSessionQNABoard' 
import { useGetOrgTrailSessionCohortsQuery } from '../orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice' 

const EtUserTrailBoardCompProd = ({
  userId,
  trailId,
  trailInfo,
  sessions = [],
  userTrailSessionExecutionData,
  onStartSession,
  userSessionDataSets = {},
}) => {

  // First Created - 01 Jul 2026
  // Author - Atul Pandey
  // Purpose - Trail board for users
  // Updated - 02 Jul 2026 - Simplified display without additional backend data load
  // Updated - 02 Jul 2026 - Added session progress timeline
  // Updated - 02 Jul 2026 - Refined current "Now" session and execution date display
  // Updated - 03 Jul 2026 - Refined Team Notes section
  // Updated - 03 Jul 2026 - Added Session Q&A section
  // Updated - 21 Jul 2026 - Refinements for note text sizes
  
  
  // 00 - Validate incoming data
  // console.log('userId', userId)
  // console.log('trailId', trailId)
  // console.log('trailInfo', trailInfo)
//   console.log('sessions', sessions)

  // 0 - Initialize navigation for direct session access from timeline
  const navigate = useNavigate()

  // 1 - State Variables
  
  // 1a -Manage the active Trail Board section.
  // Journey is the default tab.
  const [activeTrailBoardTab, setActiveTrailBoardTab] = useState('journey')

  // 1b - Manage the selected session filter in My Session Notes.
  // "all" shows notes from every session in the trail.
  const [selectedNoteSessionId, setSelectedNoteSessionId] = useState('all')

  // 1c - Manage expanded note details in My Session Notes.
  const [showTrailSessionNoteDetails, setShowTrailSessionNoteDetails] =
  useState({})

  // 1d - Manage the selected session filter in Coach's Notes.
  const [selectedCoachNoteSessionId, setSelectedCoachNoteSessionId] =
   useState('all')

  // 1e - Manage expanded Coach's Note details.
  const [showTrailCoachNoteDetails, setShowTrailCoachNoteDetails] =
   useState({})

  // 1f - Manage Coach's Notes audience filter.
  const [selectedCoachNoteAudience, setSelectedCoachNoteAudience] =
    useState('all')

  // 1g - 03 Jul 2026 - Manage the selected session filter in Team Notes.
  const [selectedTeamNoteSessionId, setSelectedTeamNoteSessionId] =
    useState('all')

  // 1h - 03 Jul 2026 - Manage expanded Team Note details.
  const [showTrailTeamNoteDetails, setShowTrailTeamNoteDetails] =
   useState({})

  // 1i - 03 Jul 2026 - Manage the selected session Q&A Board.
  const [selectedQNASessionId, setSelectedQNASessionId] =
   useState(null)

   // 21 Jul 2026 - Default text size for expanded Trail Board notes
   const [trailNoteTextSize, setTrailNoteTextSize] =
    useState('medium')

   // 21 Jul 2026 - Individual note text size overrides
   const [trailNoteTextSizeByKey, setTrailNoteTextSizeByKey] =
    useState({})

   const trailNoteTextSizeOptions = [
    {
        value: 'small',
        label: 'S',
        title: 'Small text',
    },
    {
        value: 'medium',
        label: 'M',
        title: 'Medium text',
    },
    {
        value: 'large',
        label: 'L',
        title: 'Large text',
    },
    {
        value: 'extraLarge',
        label: 'XL',
        title: 'Extra large text',
    },
   ]

   const trailNoteTextSizeClasses = {
    small: 'text-[11px] leading-5',
    medium: 'text-sm leading-6',
    large: 'text-base leading-7',
    extraLarge: 'text-lg leading-8',
    }

    const getTrailNoteTextSize = (noteKey) => {
    return (
        trailNoteTextSizeByKey[noteKey] ||
        trailNoteTextSize
    )
   }

   const getTrailNoteTextSizeClass = (noteKey) => {
    const selectedSize =
        getTrailNoteTextSize(noteKey)

    return (
        trailNoteTextSizeClasses[selectedSize] ||
        trailNoteTextSizeClasses.medium
    )
   }

   const handleTrailNoteTextSizeChange = (
    noteKey,
    selectedSize
    ) => {
    setTrailNoteTextSizeByKey((previous) => ({
        ...previous,
        [noteKey]: selectedSize,
    }))
   }

  // 2a - 03 Jul 2026 - Load complete cohort records for Session Q&A response calculations.
  //
  // The Q&A board needs the full active cohort record, including cohort users,
  // not only the cohort ID carried by a user session plan.
  const {
    data: orgTrailSessionCohortsData,
  } = useGetOrgTrailSessionCohortsQuery()

  const orgTrailSessionCohorts =
    orgTrailSessionCohortsData?.entities
        ? Object.values(orgTrailSessionCohortsData.entities)
        : []

  // 2b - Extract the user trail session execution records already loaded by parent
  const executions = userTrailSessionExecutionData?.entities
    ? Object.values(userTrailSessionExecutionData.entities)
    : []

  // console.log('executions', executions)

  // 3a1 - Toggle full note detail for a consolidated Trail Board note.
  const handleToggleTrailSessionNoteDetails = (noteId) => {
    setShowTrailSessionNoteDetails((previous) => ({
        ...previous,
        [noteId]: !previous[noteId],
    }))
  }

  // 3a2 - Toggle full Coach's Note detail in the Trail Board.
  const handleToggleTrailCoachNoteDetails = (noteId) => {
    setShowTrailCoachNoteDetails((previous) => ({
        ...previous,
        [noteId]: !previous[noteId],
    }))
  }

  // 3a3 - Toggle full Team Note detail in the Trail Board - 03 Jul 2026
  const handleToggleTrailTeamNoteDetails = (noteId) => {
    setShowTrailTeamNoteDetails((previous) => ({
        ...previous,
        [noteId]: !previous[noteId],
    }))
  }

  // 3a4 - Resolve the user's active cohort for this Trail - 03 Jul 2026
  // This cohort is displayed in the Trail Board header.
  const activeTrailCohort = orgTrailSessionCohorts.find((cohort) => {
    const cohortTrailId =
        cohort?.trailId?._id ||
        cohort?.trailId

    const hasUserInCohort =
        cohort?.cohortUserIds?.some((cohortUser) => {
        const cohortUserId =
            cohortUser?.userId?._id ||
            cohortUser?.userId

        return (
            cohortUserId?.toString() ===
            userId?.toString()
        )
        })

    return (
        cohort?.active === true &&
        cohortTrailId?.toString() ===
        trailId?.toString() &&
        hasUserInCohort
    )
  })

  // 3b - Find the matching execution record for one planned session
  // Match on user, trail, session, and cohort so the displayed status/date
  // belongs to the correct session instance.
  const getSessionExecution = (session) => {
    const sessionId = session?.sessionId?._id
    const cohortId = session?.cohortId?._id || session?.cohortId

    return executions.find((execution) => {
      const executionUserId =
        execution?.userId?._id || execution?.userId

      const executionTrailId =
        execution?.trailId?._id || execution?.trailId

      const executionSessionId =
        execution?.sessionId?._id || execution?.sessionId

      const executionCohortId =
        execution?.cohortId?._id || execution?.cohortId

      return (
        executionUserId?.toString() === userId?.toString() &&
        executionTrailId?.toString() === trailId?.toString() &&
        executionSessionId?.toString() === sessionId?.toString() &&
        executionCohortId?.toString() === cohortId?.toString()
      )
    })
  }

  // 3c - Resolve the timeline display status for a planned session
  // No execution record means the session remains Planned.
  const getTimelineStatus = (session) => {
    const execution = getSessionExecution(session)

    return execution?.userTrailSessionExecutionStatus || 'Planned'
  }

  // 4 - Resolve the best available execution date
  // Prefer dateStarted. Fall back to createdAt when dateStarted is absent.
  const getTimelineDateValue = (session) => {
    const execution = getSessionExecution(session)

    const sessionDate =
      execution?.dateStarted || execution?.createdAt

    if (!sessionDate) {
      return null
    }

    const parsedDate = new Date(sessionDate)

    if (Number.isNaN(parsedDate.getTime())) {
      return null
    }

    return parsedDate
  }

  // 5 - Format the session execution date for timeline display
  // Uses dateStarted, or createdAt where dateStarted is not available.
  const getTimelineDate = (session) => {
    const executionDate = getTimelineDateValue(session)

    if (!executionDate) {
      return null
    }

    return executionDate.toLocaleDateString(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }
    )
  }

  // 6 - Find the session closest to the current time.
  // A session qualifies only when it has an execution date on or before now.
  // Of those sessions, select the most recently started or created execution.
  const now = new Date()

  const currentSessionIndex = sessions.reduce(
    (closestSessionIndex, session, sessionIndex) => {
      const sessionDate = getTimelineDateValue(session)

      // Planned sessions and future dated sessions cannot be "Now".
      if (!sessionDate || sessionDate > now) {
        return closestSessionIndex
      }

      // First valid started execution becomes the initial candidate.
      if (closestSessionIndex === -1) {
        return sessionIndex
      }

      const closestSessionDate = getTimelineDateValue(
        sessions[closestSessionIndex]
      )

      // Replace the current candidate only when this session is closer to now.
      if (
        closestSessionDate &&
        sessionDate.getTime() > closestSessionDate.getTime()
      ) {
        return sessionIndex
      }

      return closestSessionIndex
    },
    -1
  )

  // 7 - Resolve timeline node styling based on session status
  const getTimelineNodeClasses = (session, sessionIndex) => {
    const status = getTimelineStatus(session)

    if (sessionIndex === currentSessionIndex) {
      return 'border-orange-600 bg-orange-600 text-white'
    }

    if (status === 'Completed') {
      return 'border-lime-600 bg-lime-600 text-white'
    }

    if (
      status === 'In Progress' ||
      status === 'Started'
    ) {
      return 'border-orange-500 bg-orange-100 text-orange-700'
    }

    return 'border-gray-300 bg-white text-gray-400'
  }

  // 8 - Resolve the connector line styling after each session node
  // Completed and started sessions make the journey line visibly active.
  const getTimelineLineClasses = (session) => {
    const status = getTimelineStatus(session)

    if (
      status === 'Completed' ||
      status === 'In Progress' ||
      status === 'Started'
    ) {
      return 'bg-lime-500'
    }

    return 'bg-gray-200'
  }

  // 9a - Open an existing session directly from the timeline.
  // For a planned session, use the parent start handler so the execution and
  // typed user-session record are created before navigation.
  const handleTimelineSessionClick = (session) => {
    const execution = getSessionExecution(session)

    const sessionId = session?.sessionId?._id
    const sessionTypeName =
        session?.sessionId?.sessionTypeId?.sessionTypeName?.toLowerCase()

    const cohortId =
        session?.cohortId?._id || session?.cohortId

    if (!sessionId || !sessionTypeName) {
        return
    }

    // Existing execution: session is already available to open.
    if (execution) {
        navigate(
        `/etapp/${sessionTypeName}/${userId}/${trailId}/${sessionId}`
        )

        return
    }

    // Planned session: create its execution first using the existing parent logic.
    if (onStartSession) {
        onStartSession(
        userId,
        trailId,
        sessionId,
        session?.sessionId?.sessionTypeId,
        cohortId
        )
    }
  }

  // 10 - Open the originating session with the note selected for editing.
  //
  // The note remains edited through its existing session-type-specific update flow.
  // The query parameters identify the note and let the session component open it.
  const handleEditTrailSessionNote = (note) => {
    const sessionTypeName =
        note?.sessionTypeName?.toLowerCase()

    if (
        !note?.sessionId ||
        !note?.noteId ||
        !sessionTypeName
    ) {
        return
    }

    navigate(
        `/etapp/${sessionTypeName}/${userId}/${trailId}/${note.sessionId}?editNoteId=${note.noteId}`
    )
  }

  // 9c - Remove HTML tags from trail description before rendering.
  // This prevents stored paragraph markup such as <p>...</p> from appearing.
  const getPlainTrailDescription = (description) => {
    if (!description) {
      return ''
    }

    return description
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const plainTrailDescription = getPlainTrailDescription(
    trailInfo?.trailDesc
  )

  // 10 - Convert an HTML note into a compact plain-text preview
  const getPlainNotePreview = (noteText) => {
    if (!noteText) {
        return ''
    }

    return noteText
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/p>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
  }

  // 11 - Format a note date for the consolidated Trail Board display
  const formatTrailBoardDate = (dateValue) => {
    if (!dateValue) {
        return '—'
    }

    const parsedDate = new Date(dateValue)

    if (Number.isNaN(parsedDate.getTime())) {
        return '—'
    }

    return parsedDate.toLocaleDateString(
        'en-US',
        {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        }
    )
  }

  // 12 - Identify the user-created note array in a typed user-session record.
  //
  // Examples:
  // introspectionSessionNotes
  // introductionSessionNotes
  // investigationSessionNotes
  //
  // Coach, sponsor, and team note arrays are deliberately excluded because
  // this tab is only for notes authored by the user.
  const getUserSessionNoteArrayKeys = (userSessionRecord) => {
    if (!userSessionRecord) {
        return []
    }

    return Object.keys(userSessionRecord).filter((key) => {
        const normalizedKey = key.toLowerCase()

        return (
        normalizedKey.endsWith('sessionnotes') &&
        !normalizedKey.includes('coach') &&
        !normalizedKey.includes('sponsor') &&
        !normalizedKey.includes('team')
        )
    })
  }

  // 13 - Resolve note text and dates without hardcoding each session type.
    //
    // Examples:
    // introspectionNoteText
    // introspectionNoteDate
    // introspectionNoteUpdateDate
    //
    // This lets the Trail Board consolidate notes from all typed user-session
    // collections that follow the existing note-field convention.
  const getUserSessionNoteValues = (note) => {
    const noteKeys = Object.keys(note || {})

    const noteTextKey = noteKeys.find((key) =>
        key.toLowerCase().endsWith('notetext')
    )

    const noteDateKey = noteKeys.find((key) =>
        key.toLowerCase().endsWith('notedate')
    )

    const noteUpdateDateKey = noteKeys.find((key) =>
        key.toLowerCase().endsWith('noteupdatedate')
    )

    return {
        noteText: noteTextKey ? note[noteTextKey] : '',
        noteDate: noteDateKey ? note[noteDateKey] : null,
        noteUpdateDate: noteUpdateDateKey
        ? note[noteUpdateDateKey]
        : null,
    }
  }

  // 14 - Consolidate all user-created notes from all typed user-session datasets.
  //
  // Only records belonging to the current user and current trail are included.
  // Each note is enriched with its session name and sequence for display.
  const allTrailSessionNotes = Object.values(userSessionDataSets)
    .flatMap((userSessionData) => {
        if (!userSessionData?.entities) {
        return []
        }

        return Object.values(userSessionData.entities)
    })
    .filter((userSessionRecord) => {
        const recordUserId =
        userSessionRecord?.userId?._id || userSessionRecord?.userId

        const recordTrailId =
        userSessionRecord?.trailId?._id || userSessionRecord?.trailId

        return (
        recordUserId?.toString() === userId?.toString() &&
        recordTrailId?.toString() === trailId?.toString()
        )
    })
    .flatMap((userSessionRecord) => {
        const noteArrayKeys = getUserSessionNoteArrayKeys(
        userSessionRecord
        )

        return noteArrayKeys.flatMap((noteArrayKey) => {
        const sessionNotes = userSessionRecord?.[noteArrayKey] || []

        return sessionNotes
            .filter((note) => note?.delete !== true)
            .map((note) => {
            const {
                noteText,
                noteDate,
                noteUpdateDate,
            } = getUserSessionNoteValues(note)

            return {
                noteId: note?._id,
                sessionId:
                userSessionRecord?.sessionId?._id ||
                userSessionRecord?.sessionId,
                sessionSequence:
                userSessionRecord?.sessionId?.sessionSequence,
                sessionName:
                userSessionRecord?.sessionId?.sessionName ||
                'Session',
                sessionTypeName:
                    userSessionRecord?.sessionId?.sessionTypeId?.sessionTypeName,
                sessionPic:
                    userSessionRecord?.sessionId?.pic,
                noteText,
                noteDate,
                noteUpdateDate,
            }
            })
            .filter((note) => note.noteText?.trim())
        })
    })
    .sort((firstNote, secondNote) => {
        const firstDate =
        firstNote.noteUpdateDate ||
        firstNote.noteDate ||
        0

        const secondDate =
        secondNote.noteUpdateDate ||
        secondNote.noteDate ||
        0

        return new Date(secondDate) - new Date(firstDate)
  })

  // 15 - Build one session filter chip for each session that has at least one note.
  const noteSessionFilters = sessions
    .map((session) => {
        const sessionId =
        session?.sessionId?._id || session?.sessionId

        const sessionNotesCount = allTrailSessionNotes.filter(
        (note) =>
            note?.sessionId?.toString() === sessionId?.toString()
        ).length

        return {
        sessionId,
        sessionSequence: session?.sessionId?.sessionSequence,
        sessionName: session?.sessionId?.sessionName || 'Session',
        sessionNotesCount,
        }
  })
  .filter((session) => session.sessionNotesCount > 0)

  // 16 - Filter the consolidated note list based on the selected session chip.
  const filteredTrailSessionNotes =
    selectedNoteSessionId === 'all'
        ? allTrailSessionNotes
        : allTrailSessionNotes.filter(
            (note) =>
            note?.sessionId?.toString() ===
            selectedNoteSessionId?.toString()
  )

  // 17 - Identify Coach note arrays on a typed user-session record.
  //
  // coachSessionAllUsersNotes  -> Coach note shared with the cohort
  // coachSessionSharedUserNotes -> Coach note shared specifically with this user
  const getCoachSessionNoteArrayKeys = (userSessionRecord) => {
    if (!userSessionRecord) {
        return []
    }

    return Object.keys(userSessionRecord).filter((key) => {
        const normalizedKey = key.toLowerCase()

        return (
        normalizedKey === 'coachsessionallusersnotes' ||
        normalizedKey === 'coachsessionsharedusernotes'
        )
    })
  }

  // 18 - Resolve Coach note text and dates from the populated note reference.
  //
  // All-user references use coachAllUsersNotesId.
  // User-specific references use coachSharedUserNotesId.
  const getCoachNoteValues = (coachNoteReference, noteAudience) => {
        const coachNote =
            noteAudience === 'For Everyone'
            ? (
                coachNoteReference?.coachAllUsersNotesId ||
                coachNoteReference
                )
            : (
                coachNoteReference?.coachSharedUserNotesId ||
                coachNoteReference
                )

        const noteKeys = Object.keys(coachNote || {})

        const noteTextKey = noteKeys.find((key) =>
            key.toLowerCase().endsWith('notetext')
        )

        const noteDateKey = noteKeys.find((key) =>
            key.toLowerCase().endsWith('notedate')
        )

        const noteUpdateDateKey = noteKeys.find((key) =>
            key.toLowerCase().endsWith('noteupdatedate')
        )

        return {
            noteId:
            coachNoteReference?._id ||
            coachNote?._id,

            noteText:
            noteTextKey
                ? coachNote[noteTextKey]
                : '',

            noteDate:
            noteDateKey
                ? coachNote[noteDateKey]
                : null,

            noteUpdateDate:
            noteUpdateDateKey
                ? coachNote[noteUpdateDateKey]
                : null,
        }
  }

  // 19 - Consolidate Coach's Notes from all typed session records
  // belonging to the current user and trail.
  const allTrailCoachNotes = Object.values(userSessionDataSets)
    .flatMap((userSessionData) => {
        if (!userSessionData?.entities) {
        return []
        }

        return Object.values(userSessionData.entities)
    })
    .filter((userSessionRecord) => {
        const recordUserId =
        userSessionRecord?.userId?._id ||
        userSessionRecord?.userId

        const recordTrailId =
        userSessionRecord?.trailId?._id ||
        userSessionRecord?.trailId

        return (
        recordUserId?.toString() === userId?.toString() &&
        recordTrailId?.toString() === trailId?.toString()
        )
    })
    .flatMap((userSessionRecord) => {
        const noteArrayKeys = getCoachSessionNoteArrayKeys(
        userSessionRecord
        )

        return noteArrayKeys.flatMap((noteArrayKey) => {
        const noteAudience =
            noteArrayKey === 'coachSessionAllUsersNotes'
            ? 'For Everyone'
            : 'For Me'

        const coachNotes =
            userSessionRecord?.[noteArrayKey] || []

        return coachNotes
            .map((coachNoteReference) => {
            const {
                noteId,
                noteText,
                noteDate,
                noteUpdateDate,
            } = getCoachNoteValues(
                coachNoteReference,
                noteAudience
            )

            return {
                noteId,
                noteAudience,
                sessionId:
                userSessionRecord?.sessionId?._id ||
                userSessionRecord?.sessionId,
                sessionSequence:
                userSessionRecord?.sessionId?.sessionSequence,
                sessionName:
                userSessionRecord?.sessionId?.sessionName ||
                'Session',
                sessionPic:
                userSessionRecord?.sessionId?.pic,
                noteText,
                noteDate,
                noteUpdateDate,
            }
            })
            .filter((note) => note?.noteText?.trim())
        })
  })
  .sort((firstNote, secondNote) => {
        const firstDate =
        firstNote?.noteUpdateDate ||
        firstNote?.noteDate ||
        0

        const secondDate =
        secondNote?.noteUpdateDate ||
        secondNote?.noteDate ||
        0

        return new Date(secondDate) - new Date(firstDate)
  })

  // 20 - Build Coach's Notes session filter chips.
  const coachNoteSessionFilters = sessions
    .map((session) => {
        const sessionId =
        session?.sessionId?._id ||
        session?.sessionId

        const sessionNotesCount = allTrailCoachNotes.filter(
        (note) =>
            note?.sessionId?.toString() ===
            sessionId?.toString()
        ).length

        return {
        sessionId,
        sessionSequence:
            session?.sessionId?.sessionSequence,
        sessionName:
            session?.sessionId?.sessionName ||
            'Session',
        sessionNotesCount,
        }
  })
  .filter((session) => session.sessionNotesCount > 0)

  // 21 - Filter Coach's Notes according to the selected session
  // and audience chips.
  const filteredTrailCoachNotes = allTrailCoachNotes.filter((note) => {
    const matchesSelectedSession =
        selectedCoachNoteSessionId === 'all' ||
        note?.sessionId?.toString() ===
        selectedCoachNoteSessionId?.toString()

    const matchesSelectedAudience =
        selectedCoachNoteAudience === 'all' ||
        note?.noteAudience === selectedCoachNoteAudience

    return (
        matchesSelectedSession &&
        matchesSelectedAudience
    )
  })

  // 22 - Calculate audience counts for Coach's Notes filter chips.
  const coachAllUsersNotesCount = allTrailCoachNotes.filter(
  (note) => note?.noteAudience === 'For Everyone'
  ).length

  const coachForMeNotesCount = allTrailCoachNotes.filter(
    (note) => note?.noteAudience === 'For Me'
  ).length 

  // 03 Jul 2026

  // 23 - Identify Team Note arrays on a typed user-session record.
  //
  // Examples:
  // introspectionSessionTeamNotes
  // introductionSessionTeamNotes
  // investigationSessionTeamNotes
  const getTeamSessionNoteArrayKeys = (userSessionRecord) => {
    if (!userSessionRecord) {
        return []
    }

    return Object.keys(userSessionRecord).filter((key) =>
        key.toLowerCase().endsWith('sessionteamnotes')
    )
  }

  // 24 - Resolve the original user note shared with the team.
  //
  // Team Note records carry a reference similar to:
  // originalIntrospectionSessionNoteId
  //
  // This detects that original note reference without hardcoding session types.
  const getOriginalTeamNote = (teamNoteReference) => {
    const originalNoteReferenceKey = Object.keys(
        teamNoteReference || {}
    ).find((key) => {
        const normalizedKey = key.toLowerCase()

        return (
        normalizedKey.startsWith('original') &&
        normalizedKey.endsWith('sessionnoteid')
        )
    })

    return originalNoteReferenceKey
        ? teamNoteReference?.[originalNoteReferenceKey]
        : null
  }

  // 25 - Resolve note text and dates from the original shared session note.
  const getTeamNoteValues = (teamNoteReference) => {
    const originalNote = getOriginalTeamNote(teamNoteReference)

    const noteKeys = Object.keys(originalNote || {})

    const noteTextKey = noteKeys.find((key) =>
        key.toLowerCase().endsWith('notetext')
    )

    const noteDateKey = noteKeys.find((key) =>
        key.toLowerCase().endsWith('notedate')
    )

    const noteUpdateDateKey = noteKeys.find((key) =>
        key.toLowerCase().endsWith('noteupdatedate')
    )

    return {
        noteId:
        teamNoteReference?._id ||
        originalNote?._id,

        noteText:
        noteTextKey
            ? originalNote?.[noteTextKey]
            : '',

        noteDate:
        noteDateKey
            ? originalNote?.[noteDateKey]
            : null,

        noteUpdateDate:
        noteUpdateDateKey
            ? originalNote?.[noteUpdateDateKey]
            : null,
    }
  }

  // 26 - Consolidate Team Notes from all typed user-session records
  // belonging to the current user and trail.
  const allTrailTeamNotes = Object.values(userSessionDataSets)
    .flatMap((userSessionData) => {
        if (!userSessionData?.entities) {
        return []
        }

        return Object.values(userSessionData.entities)
    })
    .filter((userSessionRecord) => {
        const recordUserId =
        userSessionRecord?.userId?._id ||
        userSessionRecord?.userId

        const recordTrailId =
        userSessionRecord?.trailId?._id ||
        userSessionRecord?.trailId

        return (
        recordUserId?.toString() === userId?.toString() &&
        recordTrailId?.toString() === trailId?.toString()
        )
    })
    .flatMap((userSessionRecord) => {
        const noteArrayKeys = getTeamSessionNoteArrayKeys(
        userSessionRecord
        )

        return noteArrayKeys.flatMap((noteArrayKey) => {
        const teamNotes =
            userSessionRecord?.[noteArrayKey] || []

        return teamNotes
            .map((teamNoteReference) => {
            const {
                noteId,
                noteText,
                noteDate,
                noteUpdateDate,
            } = getTeamNoteValues(teamNoteReference)

            const sharedBy =
                teamNoteReference?.noteSharedBy || {}

            return {
                noteId,
                sessionId:
                userSessionRecord?.sessionId?._id ||
                userSessionRecord?.sessionId,
                sessionSequence:
                userSessionRecord?.sessionId?.sessionSequence,
                sessionName:
                userSessionRecord?.sessionId?.sessionName ||
                'Session',
                sessionPic:
                userSessionRecord?.sessionId?.pic,
                sharedByName:
                sharedBy?.name || 'Team Member',
                sharedByUsername:
                sharedBy?.username || '',
                sharedByPic:
                sharedBy?.pic || '',
                noteText,
                noteDate,
                noteUpdateDate,
            }
            })
            .filter((note) => note?.noteText?.trim())
        })
    })
    .sort((firstNote, secondNote) => {
        const firstDate =
        firstNote?.noteUpdateDate ||
        firstNote?.noteDate ||
        0

        const secondDate =
        secondNote?.noteUpdateDate ||
        secondNote?.noteDate ||
        0

        return new Date(secondDate) - new Date(firstDate)
  })

  // 27 - Build Team Notes session filter chips.
  const teamNoteSessionFilters = sessions
    .map((session) => {
        const sessionId =
        session?.sessionId?._id ||
        session?.sessionId

        const sessionNotesCount = allTrailTeamNotes.filter(
        (note) =>
            note?.sessionId?.toString() ===
            sessionId?.toString()
        ).length

        return {
        sessionId,
        sessionSequence:
            session?.sessionId?.sessionSequence,
        sessionName:
            session?.sessionId?.sessionName ||
            'Session',
        sessionNotesCount,
        }
    })
  .filter((session) => session.sessionNotesCount > 0)

  // 28 - Filter Team Notes by the selected session chip.
  const filteredTrailTeamNotes =
    selectedTeamNoteSessionId === 'all'
        ? allTrailTeamNotes
        : allTrailTeamNotes.filter(
            (note) =>
            note?.sessionId?.toString() ===
            selectedTeamNoteSessionId?.toString()
  )

  // 29 - Resolve the user's organization from the typed session datasets.
  // This is required by EtUserSessionQNABoard.
  const trailBoardUserOrgId = Object.values(userSessionDataSets)
    .flatMap((userSessionData) => {
        if (!userSessionData?.entities) {
        return []
        }

        return Object.values(userSessionData.entities)
    })
    .find((userSessionRecord) => {
        const recordUserId =
        userSessionRecord?.userId?._id ||
        userSessionRecord?.userId

        const recordTrailId =
        userSessionRecord?.trailId?._id ||
        userSessionRecord?.trailId

        return (
        recordUserId?.toString() === userId?.toString() &&
        recordTrailId?.toString() === trailId?.toString()
        )
    })
    ?.userId?.userOrgId?._id ||
    Object.values(userSessionDataSets)
        .flatMap((userSessionData) => {
        if (!userSessionData?.entities) {
            return []
        }

        return Object.values(userSessionData.entities)
        })
        .find((userSessionRecord) => {
        const recordUserId =
            userSessionRecord?.userId?._id ||
            userSessionRecord?.userId

        const recordTrailId =
            userSessionRecord?.trailId?._id ||
            userSessionRecord?.trailId

        return (
            recordUserId?.toString() === userId?.toString() &&
            recordTrailId?.toString() === trailId?.toString()
        )
      })
  ?.userId?.userOrgId

  // 29a - Resolve the complete active cohort for a Trail Board Q&A session.
  //
  // This mirrors the individual session page logic so EtUserSessionQNABoard
  // receives the same full cohort context in both locations.
  const getFullQNASessionCohort = (sessionCohortId) => {
    if (!sessionCohortId) {
        return null
    }

    return orgTrailSessionCohorts.find((cohort) => {
        const cohortId =
        cohort?._id?.toString()

        const cohortTrailId =
        cohort?.trailId?._id ||
        cohort?.trailId

        const hasUserInCohort =
        cohort?.cohortUserIds?.some((cohortUser) => {
            const cohortUserId =
            cohortUser?.userId?._id ||
            cohortUser?.userId

            return (
            cohortUserId?.toString() ===
            userId?.toString()
            )
        })

        return (
        cohort?.active === true &&
        cohortId === sessionCohortId?.toString() &&
        cohortTrailId?.toString() === trailId?.toString() &&
        hasUserInCohort
        )
    })
  }

  // 30 - Build one Q&A tab for every trail session that has active Q&A sets.
  const sessionQNASessions = sessions
    .map((session) => {
        const sessionId =
        session?.sessionId?._id ||
        session?.sessionId

        const sessionQNASets =
        session?.sessionId?.sessionQNASets ||
        []

        const activeSessionQNASets = sessionQNASets.filter(
        (item) => item?.active !== false
        )

        return {
        sessionId,
        sessionSequence:
            session?.sessionId?.sessionSequence,
        sessionName:
            session?.sessionId?.sessionName ||
            'Session',
        sessionPic:
            session?.sessionId?.pic,
        sessionQNASets: activeSessionQNASets,
        cohortId:
            session?.cohortId?._id ||
            session?.cohortId,
        cohort: getFullQNASessionCohort(
            session?.cohortId?._id ||
            session?.cohortId
            ),
        }
  })
    .filter(
        (session) =>
        session?.sessionQNASets?.length > 0
  )

//   console.log('sessionQNASessions', sessionQNASessions)

  // 31 - Use the selected Q&A session, or default to the first available board.
  const resolvedSelectedQNASessionId =
    selectedQNASessionId ||
    sessionQNASessions?.[0]?.sessionId

    const selectedQNASession =
    sessionQNASessions.find(
        (session) =>
        session?.sessionId?.toString() ===
        resolvedSelectedQNASessionId?.toString()
  )

  // 21 Jul 2026 - Text size selector for one expanded Trail Board note
  const renderTrailNoteTextSizeSelector = (noteKey) => {
    const selectedSize =
        getTrailNoteTextSize(noteKey)

    return (
        <div className="mb-2 flex justify-end">
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1">
            <p className="mr-1 text-[9px] font-semibold text-gray-500">
            Text:
            </p>

            {trailNoteTextSizeOptions.map((sizeOption) => {
            const isSelected =
                selectedSize === sizeOption.value

            return (
                <button
                key={sizeOption.value}
                type="button"
                title={sizeOption.title}
                aria-label={sizeOption.title}
                aria-pressed={isSelected}
                onClick={() =>
                    handleTrailNoteTextSizeChange(
                    noteKey,
                    sizeOption.value
                    )
                }
                className={`min-w-7 rounded px-2 py-1 text-[9px] font-semibold transition ${
                    isSelected
                    ? 'bg-orange-600 text-white shadow-sm'
                    : 'border border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:text-orange-600'
                }`}
                >
                {sizeOption.label}
                </button>
            )
            })}
        </div>
        </div>
    )
  }

  return (
    <div className="w-full space-y-2 rounded bg-gray-100 p-3 sm:p-5">

      {/* 21 Jul 2026 - Default size for all expanded Trail Board notes */}
      <div className="flex justify-end">
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 shadow-sm">
            <p className="mr-1 text-[9px] font-semibold text-gray-500">
            Note text:
            </p>

            {trailNoteTextSizeOptions.map((sizeOption) => {
            const isSelected =
                trailNoteTextSize === sizeOption.value

            return (
                <button
                key={sizeOption.value}
                type="button"
                title={sizeOption.title}
                aria-label={sizeOption.title}
                aria-pressed={isSelected}
                onClick={() => {
                    setTrailNoteTextSize(sizeOption.value)
                    setTrailNoteTextSizeByKey({})
                }}
                className={`min-w-7 rounded px-2 py-1 text-[9px] font-semibold transition ${
                    isSelected
                    ? 'bg-orange-600 text-white shadow-sm'
                    : 'border border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:text-orange-600'
                }`}
                >
                {sizeOption.label}
                </button>
            )
            })}
        </div>
      </div>

      {/* 10 - Trail header section */}
      <div className="rounded bg-white p-3 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <div className="flex items-center justify-start">
            {trailInfo?.pic && (
                <div className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50">
                <img
                    src={trailInfo.pic}
                    alt=""
                    className="h-7 w-7"
                />
                </div>
            )}

            <div>
                <p className="text-sm font-bold text-gray-800">
                {trailInfo?.trailName || 'Trail Overview'}
                </p>

                {plainTrailDescription && (
                <p className="text-[10px] text-gray-500">
                    {plainTrailDescription}
                </p>
                )}
            </div>
            </div>

            {/* 10a - Active cohort details */}
            {activeTrailCohort && (
            <div className="flex shrink-0 items-center rounded border border-orange-200 bg-orange-50 px-2 py-1">
                <div className="mr-1 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                <UserGroupIcon className="h-3.5 w-3.5 text-orange-600" />
                </div>

                <div>
                <p className="text-[8px] font-semibold text-gray-500">
                    Engagement Cohort
                </p>

                <div className="flex items-center">
                    <p className="text-[10px] font-bold text-orange-700">
                    {activeTrailCohort?.cohortName || 'Cohort'}
                    </p>

                    {activeTrailCohort?.cohortCode && (
                    <p className="ml-1 text-[8px] italic text-gray-500">
                        ({activeTrailCohort.cohortCode})
                    </p>
                    )}
                </div>
                </div>
            </div>
            )}
        </div>

        {/* 10b - Cohort description */}
        {activeTrailCohort?.cohortDesc && (
            <div className="mt-2 border-t border-orange-100 pt-2">
            <p className="text-[10px] text-gray-500">
                {activeTrailCohort.cohortDesc}
            </p>
            </div>
        )}
      </div>

      {/* 11 - Trail Board section tabs */}
      <div className="rounded bg-white px-3 p-2 shadow-sm">
        <div className="flex overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTrailBoardTab('journey')}
            className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
              activeTrailBoardTab === 'journey'
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Journey
          </button>

          <button
            type="button"
            onClick={() => setActiveTrailBoardTab('mySessionNotes')}
            className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
              activeTrailBoardTab === 'mySessionNotes'
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            My Session Notes
          </button>

          <button
            type="button"
            onClick={() => setActiveTrailBoardTab('coachNotes')}
            className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
              activeTrailBoardTab === 'coachNotes'
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Coach&apos;s Notes
          </button>

          <button
            type="button"
            onClick={() => setActiveTrailBoardTab('teamNotes')}
            className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
              activeTrailBoardTab === 'teamNotes'
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Team Notes
          </button>

          <button
            type="button"
            onClick={() => setActiveTrailBoardTab('sessionQuestions')}
            className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
              activeTrailBoardTab === 'sessionQuestions'
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Session Q&As
          </button>

          {/* <button
            type="button"
            onClick={() => setActiveTrailBoardTab('actions')}
            className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
              activeTrailBoardTab === 'actions'
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Actions
          </button> */}
        </div>
      </div>

      {/* 12 - Journey timeline section */}
      {activeTrailBoardTab === 'journey' && (
        <div className="rounded bg-white p-3 shadow-sm">
            <div className="mb-5">
            <p className="text-sm font-bold text-gray-800">
                Journey Timeline
            </p>

            <p className="text-[10px] text-gray-500">
                Your progress through the planned trail sessions.
            </p>
            </div>

            {sessions.length > 0 ? (
            <div className="overflow-x-auto pb-3">
                <div className="flex min-w-max items-start px-2">

                {/* 12a - Render one timeline node per planned session */}
                {sessions.map((session, sessionIndex) => {
                    const status = getTimelineStatus(session)
                    const startedDate = getTimelineDate(session)

                    const isCurrentSession =
                    sessionIndex === currentSessionIndex

                    return (
                    <React.Fragment
                        key={
                        session?._id ||
                        session?.sessionId?._id ||
                        sessionIndex
                        }
                    >
                        <button
                            type="button"
                            onClick={() => handleTimelineSessionClick(session)}
                            className="group flex w-28 shrink-0 flex-col items-center rounded p-1 text-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
                            title={
                                getSessionExecution(session)
                                ? `Open ${session?.sessionId?.sessionName || 'session'}`
                                : `Start ${session?.sessionId?.sessionName || 'session'}`
                            }
                        >
                            {/* 12b - Clickable session sequence node */}
                            <div
                                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-transform group-hover:scale-110 ${getTimelineNodeClasses(
                                session,
                                sessionIndex
                                )}`}
                            >
                                {session?.sessionId?.sessionSequence ||
                                sessionIndex + 1}
                            </div>

                            {/* 12c - Session name */}
                            <p className="mt-2 min-h-[30px] px-1 text-[10px] font-bold text-gray-700 group-hover:text-orange-700">
                                {session?.sessionId?.sessionName || 'Session'}
                            </p>

                            {/* 12d - Session progress status */}
                            <p
                                className={`mt-1 rounded-full px-2 py-0.5 text-[9px] font-medium ${
                                isCurrentSession
                                    ? 'bg-orange-100 text-orange-700'
                                    : status === 'Completed'
                                    ? 'bg-green-100 text-green-700'
                                    : (
                                        status === 'In Progress' ||
                                        status === 'Started'
                                    )
                                    ? 'bg-lime-100 text-lime-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                {isCurrentSession ? 'Now' : status}
                            </p>

                            {/* 12e - Execution date: dateStarted or createdAt */}
                            <p className="mt-1 min-h-[15px] text-[9px] text-gray-500">
                                {startedDate || 'Not started'}
                            </p>
                        </button>

                        {/* 12f - Connector line between timeline nodes */}
                        {sessionIndex < sessions.length - 1 && (
                        <div className="flex w-12 shrink-0 items-start pt-3">
                            <div
                            className={`h-1 w-full rounded ${getTimelineLineClasses(
                                session
                            )}`}
                            />
                        </div>
                        )}
                    </React.Fragment>
                    )
                })}
                </div>
            </div>
            ) : (
            <p className="text-xs text-gray-500">
                No sessions are planned for this trail.
            </p>
            )}
        </div>
      )}

      {/* 13 - Consolidated user-created notes across all Trail sessions */}
      {activeTrailBoardTab === 'mySessionNotes' && (
        <div className="rounded bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-start justify-between">
                <div>
                    <p className="text-sm font-bold text-gray-800">
                    My Session Notes
                    </p>

                    <p className="text-[10px] text-gray-500">
                    Notes captured across all sessions in this trail.
                    </p>
                </div>

                <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                    <p className="text-[10px] font-bold text-orange-600">
                    {filteredTrailSessionNotes.length}
                    </p>
                </div>
            </div>

            {/* 13a - Session-based note filter chips */}
            {noteSessionFilters.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
                <button
                type="button"
                onClick={() => setSelectedNoteSessionId('all')}
                className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedNoteSessionId === 'all'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                >
                All ({allTrailSessionNotes.length})
                </button>

                {noteSessionFilters.map((session) => {
                const isSelected =
                    selectedNoteSessionId?.toString() ===
                    session?.sessionId?.toString()

                return (
                    <button
                    key={session.sessionId}
                    type="button"
                    onClick={() =>
                        setSelectedNoteSessionId(session.sessionId)
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        isSelected
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    {session.sessionSequence
                        ? `Session ${session.sessionSequence}`
                        : session.sessionName}{' '}
                    ({session.sessionNotesCount})
                    </button>
                )
                })}
            </div>
            )}

            {filteredTrailSessionNotes.length > 0 ? (
            <div className="overflow-x-auto">
                <div className="min-w-[950px]">

                {/* 13a - Notes table header */}
                <div className="grid grid-cols-12 rounded bg-gray-100 px-2 py-2">
                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Session
                    </p>
                    </div>

                    <div className="col-span-5">
                    <p className="text-[10px] font-bold text-gray-600">
                        Note
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        First Created
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        Last Updated
                    </p>
                    </div>
                </div>

                {/* 13b - Consolidated session note rows */}
                {filteredTrailSessionNotes.map((note, noteIndex) => {
                    
                    const expandedNoteKey =
                    `user-note-${note?.noteId}-${note?.sessionId}` // 21 Jul 2026
                    
                    const firstCreatedDate = note?.noteDate

                    const lastUpdatedDate =
                    note?.noteUpdateDate ||
                    note?.noteDate

                    return (
                        <div

                            // 21 Jul 2026
                            key={
                                expandedNoteKey ||
                                `${note.sessionId}-${noteIndex}`
                            }
                            // key={
                            //     note.noteId ||
                            //     `${note.sessionId}-${noteIndex}`
                            // }
                        className="border-b border-gray-100 px-2 py-2 last:border-b-0 hover:bg-orange-50"
                        >
                            <div className="grid grid-cols-12 items-center">
                                {/* 13b1 - Toggle, session icon, sequence, and session name */}
                                <div className="col-span-3 flex items-center pr-2">

                                {/* 13b1a - Display note number */}
                                
                                <p className="text-md font-bold text-gray-800">
                                {noteIndex + 1}.
                                </p>
                                
                                <button
                                    type="button"
                                    onClick={() =>
                                    handleToggleTrailSessionNoteDetails(note.noteId)
                                    }
                                    className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                                    title={
                                    showTrailSessionNoteDetails[note.noteId]
                                        ? 'Hide note detail'
                                        : 'Show note detail'
                                    }
                                >
                                    {showTrailSessionNoteDetails[note.noteId] ? (
                                    <MinusIcon className="h-4 w-4 text-orange-600" />
                                    ) : (
                                    <PlusIcon className="h-4 w-4 text-orange-600" />
                                    )}
                                </button>

                                

                                <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-50">
                                    {note?.sessionPic ? (
                                    <img
                                        src={note.sessionPic}
                                        alt=""
                                        className="h-5 w-5"
                                    />
                                    ) : (
                                    <p className="text-[10px] font-bold text-gray-400">
                                        {note?.sessionSequence || '?'}
                                    </p>
                                    )}

                                    


                                </div>

                                <div>
                                    <p className="text-[10px] font-bold text-gray-700">
                                    {note?.sessionSequence
                                        ? `Session ${note.sessionSequence}`
                                        : 'Session'}
                                    </p>

                                    <p className="text-[10px] text-orange-600">
                                    {note?.sessionName}
                                    </p>
                                </div>
                                </div>

                                {/* 13b2 - Compact note preview */}
                                <div className="col-span-5 pr-3">
                                <p className="line-clamp-2 text-[11px] text-gray-700">
                                    {getPlainNotePreview(note?.noteText)}
                                </p>
                                </div>

                                {/* 13b3 - First created timestamp */}
                                <div className="col-span-2 flex items-center">
                                <ClockIcon className="mr-1 h-4 w-4 shrink-0 text-lime-600" />

                                <p className="text-[10px] text-gray-600">
                                    {formatTrailBoardDate(note?.noteDate)}
                                </p>
                                </div>

                                {/* 13b4 - Relative last-updated chip */}
                                <div className="col-span-2 flex items-center justify-between">
                                <div>
                                    {note?.noteUpdateDate || note?.noteDate ? (
                                    <>
                                        <p className="w-fit rounded-full bg-lime-200 px-2 py-[1px] text-[9px] text-gray-700">
                                        {getTimeSince(
                                            note?.noteUpdateDate || note?.noteDate
                                        )}
                                        </p>

                                        <p className="mt-0.5 text-[7px] italic text-gray-400">
                                        last updated
                                        </p>
                                    </>
                                    ) : (
                                    <p className="text-[10px] text-gray-400">
                                        —
                                    </p>
                                    )}
                                </div>

                                {/* 13b5 - Edit in the originating session */}
                                <button
                                    type="button"
                                    onClick={() => handleEditTrailSessionNote(note)}
                                    className="ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-orange-50"
                                    title="Edit note"
                                >
                                    <PencilSquareIcon className="h-4 w-4 text-orange-600" />
                                </button>
                                </div>
                            </div>

                            {/* 13b6 - Expanded full note detail */}

                            {/* 21 Jul 2026 */}
                            {showTrailSessionNoteDetails[note.noteId] && (
                            <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                                {renderTrailNoteTextSizeSelector(
                                expandedNoteKey
                                )}

                                <div
                                dangerouslySetInnerHTML={{
                                    __html: note?.noteText || '',
                                }}
                                className={`fix-nested-lists ${getTrailNoteTextSizeClass(
                                    expandedNoteKey
                                )} text-gray-700`}
                                />
                            </div>
                            )}

                            {/* {showTrailSessionNoteDetails[note.noteId] && (
                                <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                                <div
                                    dangerouslySetInnerHTML={{
                                    __html: note?.noteText || '',
                                    }}
                                    className="fix-nested-lists text-[11px] text-gray-700"
                                />
                                </div>
                            )} */}
                        </div>
                    )
                })}
                </div>
            </div>
            ) : (
            <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                {selectedNoteSessionId === 'all'
                    ? 'No session notes have been created in this trail yet.'
                    : 'No notes have been created for the selected session yet.'}
                </p>
            </div>
            )}
        </div>
      )}

      {/* 14 - Consolidated Coach's Notes across all Trail sessions */}
      {activeTrailBoardTab === 'coachNotes' && (
        <div className="rounded bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-start justify-between">
            <div>
                <p className="text-sm font-bold text-gray-800">
                Coach&apos;s Notes
                </p>

                <p className="text-[10px] text-gray-500">
                Guidance shared by your coaches across this trail.
                </p>
            </div>

            <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                <p className="text-[10px] font-bold text-orange-600">
                {filteredTrailCoachNotes.length}
                </p>
            </div>
            </div>

            {/* 14a - Coach's Notes filters: session on left, audience on right */}
            {allTrailCoachNotes.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">

                {/* 14a1 - Session filter chips */}
                <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() =>
                    setSelectedCoachNoteSessionId('all')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCoachNoteSessionId === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    All ({allTrailCoachNotes.length})
                </button>

                {coachNoteSessionFilters.map((session) => {
                    const isSelected =
                    selectedCoachNoteSessionId?.toString() ===
                    session?.sessionId?.toString()

                    return (
                    <button
                        key={session.sessionId}
                        type="button"
                        onClick={() =>
                        setSelectedCoachNoteSessionId(
                            session.sessionId
                        )
                        }
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                    >
                        {session.sessionSequence
                        ? `Session ${session.sessionSequence}`
                        : session.sessionName}{' '}
                        ({session.sessionNotesCount})
                    </button>
                    )
                })}
                </div>

                {/* 14a2 - Coach note audience filter chips */}
                <div className="flex flex-wrap justify-end gap-2">
                <button
                    type="button"
                    onClick={() =>
                    setSelectedCoachNoteAudience('all')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCoachNoteAudience === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    All Audience ({allTrailCoachNotes.length})
                </button>

                <button
                    type="button"
                    onClick={() =>
                    setSelectedCoachNoteAudience('For Me')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCoachNoteAudience === 'For Me'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    For Me ({coachForMeNotesCount})
                </button>

                <button
                    type="button"
                    onClick={() =>
                    setSelectedCoachNoteAudience('For Everyone')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCoachNoteAudience === 'For Everyone'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    For Everyone ({coachAllUsersNotesCount})
                </button>
                </div>
            </div>
            )}

            {filteredTrailCoachNotes.length > 0 ? (
            <div className="overflow-x-auto">
                <div className="min-w-[1050px]">

                {/* 14b - Coach's Notes table header */}
                <div className="grid grid-cols-12 rounded bg-gray-100 px-2 py-2">
                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Session
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        Shared With
                    </p>
                    </div>

                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Note
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        First Created
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        Last Updated
                    </p>
                    </div>
                </div>

                {/* 14c - Consolidated Coach's Note rows */}
                {filteredTrailCoachNotes.map((note, noteIndex) => {

                    const expandedNoteKey =
                    `coach-note-${note.noteId}-${note.sessionId}-${note.noteAudience}` // 21 Jul 2026
                    
                    // const expandedNoteKey =
                    // `${note.noteId}-${note.sessionId}-${note.noteAudience}`

                    const firstCreatedDate = note?.noteDate

                    const lastUpdatedDate =
                    note?.noteUpdateDate ||
                    note?.noteDate

                    return (
                    <div
                        key={expandedNoteKey}
                        className="border-b border-gray-100 px-2 py-2 last:border-b-0 hover:bg-orange-50"
                    >
                        <div className="grid grid-cols-12 items-center">

                        {/* 14c1 - Number, toggle, session picture, and name */}
                        <div className="col-span-3 flex items-center pr-2">
                            <p className="text-md font-bold text-gray-800">
                            {noteIndex + 1}.
                            </p>

                            <button
                            type="button"
                            onClick={() =>
                                handleToggleTrailCoachNoteDetails(
                                expandedNoteKey
                                )
                            }
                            className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                            title={
                                showTrailCoachNoteDetails[expandedNoteKey]
                                ? 'Hide Coach note detail'
                                : 'Show Coach note detail'
                            }
                            >
                            {showTrailCoachNoteDetails[
                                expandedNoteKey
                            ] ? (
                                <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                                <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                            </button>

                            <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-50">
                            {note?.sessionPic ? (
                                <img
                                src={note.sessionPic}
                                alt=""
                                className="h-5 w-5"
                                />
                            ) : (
                                <p className="text-[10px] font-bold text-gray-400">
                                {note?.sessionSequence || '?'}
                                </p>
                            )}
                            </div>

                            <div>
                            <p className="text-[10px] font-bold text-gray-700">
                                {note?.sessionSequence
                                ? `Session ${note.sessionSequence}`
                                : 'Session'}
                            </p>

                            <p className="text-[10px] text-orange-600">
                                {note?.sessionName}
                            </p>
                            </div>
                        </div>

                        {/* 14c2 - Audience label */}
                        <div className="col-span-2">
                            <p
                            className={`w-fit rounded-full px-2 py-1 text-[9px] font-semibold ${
                                note?.noteAudience === 'For Me'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-yellow-100 text-gray-800'
                            }`}
                            >
                            {note?.noteAudience}
                            </p>
                        </div>

                        {/* 14c3 - Compact Coach note preview */}
                        <div className="col-span-3 pr-3">
                            <p className="line-clamp-2 text-[11px] text-gray-700">
                            {getPlainNotePreview(note?.noteText)}
                            </p>
                        </div>

                        {/* 14c4 - First-created timestamp */}
                        <div className="col-span-2 flex items-center">
                            <ClockIcon className="mr-1 h-4 w-4 shrink-0 text-lime-600" />

                            <p className="text-[10px] text-gray-600">
                            {formatTrailBoardDate(firstCreatedDate)}
                            </p>
                        </div>

                        {/* 14c5 - Relative last-updated chip */}
                        <div className="col-span-2">
                            {lastUpdatedDate ? (
                            <>
                                <p className="w-fit rounded-full bg-lime-200 px-2 py-[1px] text-[9px] text-gray-700">
                                {getTimeSince(lastUpdatedDate)}
                                </p>

                                <p className="mt-0.5 text-[7px] italic text-gray-400">
                                last updated
                                </p>
                            </>
                            ) : (
                            <p className="text-[10px] text-gray-400">
                                —
                            </p>
                            )}
                        </div>
                        </div>

                        {/* 14c6 - Expanded full Coach note detail */}
                        {/* 21 Jul 2026 */}
                        {showTrailCoachNoteDetails[expandedNoteKey] && (
                        <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                            {renderTrailNoteTextSizeSelector(
                            expandedNoteKey
                            )}

                            <div
                            dangerouslySetInnerHTML={{
                                __html: note?.noteText || '',
                            }}
                            className={`fix-nested-lists ${getTrailNoteTextSizeClass(
                                expandedNoteKey
                            )} text-gray-700`}
                            />
                        </div>
                        )}


                        {/* {showTrailCoachNoteDetails[expandedNoteKey] && (
                        <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                            <div
                            dangerouslySetInnerHTML={{
                                __html: note?.noteText || '',
                            }}
                            className="fix-nested-lists text-[11px] text-gray-700"
                            />
                        </div>
                        )} */}
                    </div>
                    )
                })}
                </div>
            </div>
            ) : (
            <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                {selectedCoachNoteSessionId === 'all'
                    ? 'No Coach notes are available in this trail yet.'
                    : 'No Coach notes are available for the selected session yet.'}
                </p>
            </div>
            )}
        </div>
      )}

      {/* 15 - Consolidated Team Notes across all Trail sessions */}
      {activeTrailBoardTab === 'teamNotes' && (
        <div className="rounded bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-start justify-between">
            <div>
                <p className="text-sm font-bold text-gray-800">
                Team Notes
                </p>

                <p className="text-[10px] text-gray-500">
                Notes shared by you and your team across this trail.
                </p>
            </div>

            <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                <p className="text-[10px] font-bold text-orange-600">
                {filteredTrailTeamNotes.length}
                </p>
            </div>
            </div>

            {/* 15a - Session-based Team Notes filter chips */}
            {teamNoteSessionFilters.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
                <button
                type="button"
                onClick={() =>
                    setSelectedTeamNoteSessionId('all')
                }
                className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedTeamNoteSessionId === 'all'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                >
                All ({allTrailTeamNotes.length})
                </button>

                {teamNoteSessionFilters.map((session) => {
                const isSelected =
                    selectedTeamNoteSessionId?.toString() ===
                    session?.sessionId?.toString()

                return (
                    <button
                    key={session.sessionId}
                    type="button"
                    onClick={() =>
                        setSelectedTeamNoteSessionId(
                        session.sessionId
                        )
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        isSelected
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    {session.sessionSequence
                        ? `Session ${session.sessionSequence}`
                        : session.sessionName}{' '}
                    ({session.sessionNotesCount})
                    </button>
                )
                })}
            </div>
            )}

            {filteredTrailTeamNotes.length > 0 ? (
            <div className="overflow-x-auto">
                <div className="min-w-[1100px]">

                {/* 15b - Team Notes table header */}
                <div className="grid grid-cols-12 rounded bg-gray-100 px-2 py-2">
                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Session
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        Shared By
                    </p>
                    </div>

                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Note
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        First Created
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        Last Updated
                    </p>
                    </div>
                </div>

                {/* 15c - Consolidated Team Note rows */}
                {filteredTrailTeamNotes.map((note, noteIndex) => {

                    const expandedNoteKey =
                    `team-note-${note.noteId}-${note.sessionId}`    // 21 Jul 2026
                    
                    // const expandedNoteKey =
                    // `${note.noteId}-${note.sessionId}`

                    const lastUpdatedDate =
                    note?.noteUpdateDate ||
                    note?.noteDate

                    const firstName =
                    note?.sharedByName?.split(' ')?.[0] ||
                    'Team Member'

                    return (
                    <div
                        key={expandedNoteKey}
                        className="border-b border-gray-100 px-2 py-2 last:border-b-0 hover:bg-orange-50"
                    >
                        <div className="grid grid-cols-12 items-center">

                        {/* 15c1 - Number, toggle, session picture, and name */}
                        <div className="col-span-3 flex items-center pr-2">
                            <p className="text-md font-bold text-gray-800">
                            {noteIndex + 1}.
                            </p>

                            <button
                            type="button"
                            onClick={() =>
                                handleToggleTrailTeamNoteDetails(
                                expandedNoteKey
                                )
                            }
                            className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                            title={
                                showTrailTeamNoteDetails[expandedNoteKey]
                                ? 'Hide Team Note detail'
                                : 'Show Team Note detail'
                            }
                            >
                            {showTrailTeamNoteDetails[
                                expandedNoteKey
                            ] ? (
                                <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                                <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                            </button>

                            <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-50">
                            {note?.sessionPic ? (
                                <img
                                src={note.sessionPic}
                                alt=""
                                className="h-5 w-5"
                                />
                            ) : (
                                <p className="text-[10px] font-bold text-gray-400">
                                {note?.sessionSequence || '?'}
                                </p>
                            )}
                            </div>

                            <div>
                            <p className="text-[10px] font-bold text-gray-700">
                                {note?.sessionSequence
                                ? `Session ${note.sessionSequence}`
                                : 'Session'}
                            </p>

                            <p className="text-[10px] text-orange-600">
                                {note?.sessionName}
                            </p>
                            </div>
                        </div>

                        {/* 15c2 - Team member who shared the note */}
                        <div className="col-span-2 flex items-center">
                            <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200">
                            {note?.sharedByPic ? (
                                <img
                                src={note.sharedByPic}
                                alt=""
                                className="h-6 w-6 rounded-full"
                                />
                            ) : (
                                <p className="text-[9px] font-bold text-orange-600">
                                {firstName.charAt(0)}
                                </p>
                            )}
                            </div>

                            <p className="text-[10px] font-semibold text-gray-700">
                            {firstName}
                            </p>
                        </div>

                        {/* 15c3 - Compact Team Note preview */}
                        <div className="col-span-3 pr-3">
                            <p className="line-clamp-2 text-[11px] text-gray-700">
                            {getPlainNotePreview(note?.noteText)}
                            </p>
                        </div>

                        {/* 15c4 - First-created timestamp */}
                        <div className="col-span-2 flex items-center">
                            <ClockIcon className="mr-1 h-4 w-4 shrink-0 text-lime-600" />

                            <p className="text-[10px] text-gray-600">
                            {formatTrailBoardDate(note?.noteDate)}
                            </p>
                        </div>

                        {/* 15c5 - Relative last-updated chip */}
                        <div className="col-span-2">
                            {lastUpdatedDate ? (
                            <>
                                <p className="w-fit rounded-full bg-lime-200 px-2 py-[1px] text-[9px] text-gray-700">
                                {getTimeSince(lastUpdatedDate)}
                                </p>

                                <p className="mt-0.5 text-[7px] italic text-gray-400">
                                last updated
                                </p>
                            </>
                            ) : (
                            <p className="text-[10px] text-gray-400">
                                —
                            </p>
                            )}
                        </div>
                        </div>

                        {/* 15c6 - Expanded full Team Note detail */}
                        {/* 21 Jul 2026 */}
                        {showTrailTeamNoteDetails[expandedNoteKey] && (
                        <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                            {renderTrailNoteTextSizeSelector(
                            expandedNoteKey
                            )}

                            <div
                            dangerouslySetInnerHTML={{
                                __html: note?.noteText || '',
                            }}
                            className={`fix-nested-lists ${getTrailNoteTextSizeClass(
                                expandedNoteKey
                            )} text-gray-700`}
                            />
                        </div>
                        )}
                        
                        {/* {showTrailTeamNoteDetails[expandedNoteKey] && (
                        <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                            <div
                            dangerouslySetInnerHTML={{
                                __html: note?.noteText || '',
                            }}
                            className="fix-nested-lists text-[11px] text-gray-700"
                            />
                        </div>
                        )} */}
                    </div>
                    )
                })}
                </div>
            </div>
            ) : (
            <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                {selectedTeamNoteSessionId === 'all'
                    ? 'No Team Notes are available in this trail yet.'
                    : 'No Team Notes are available for the selected session yet.'}
                </p>
            </div>
            )}
        </div>
      )}

      {/* 16 - Session Q&A Boards across the Trail */}
      {activeTrailBoardTab === 'sessionQuestions' && (
        <div className="rounded bg-white p-3 shadow-sm">
            <div className="mb-2">
            <p className="text-sm font-bold text-gray-800">
                Session Q&amp;As
            </p>

            <p className="text-[10px] text-gray-500">
                Respond to and view the Q&amp;A boards available in each session.
            </p>
            </div>

            {sessionQNASessions.length > 0 ? (
            <>
                {/* 16a - Q&A session selection tabs */}
                <div className="mb-3 flex overflow-x-auto border-b border-gray-200">
                {sessionQNASessions.map((session) => {
                    const isSelected =
                    session?.sessionId?.toString() ===
                    resolvedSelectedQNASessionId?.toString()

                    return (
                    <button
                        key={session.sessionId}
                        type="button"
                        onClick={() =>
                        setSelectedQNASessionId(
                            session.sessionId
                        )
                        }
                        className={`flex shrink-0 items-center border-b-2 px-3 py-2 text-[10px] font-bold ${
                        isSelected
                            ? 'border-orange-600 text-orange-700'
                            : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <div className="mr-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-100">
                        {session?.sessionPic ? (
                            <img
                            src={session.sessionPic}
                            alt=""
                            className="h-4 w-4"
                            />
                        ) : (
                            <p className="text-[8px] font-bold text-gray-400">
                            {session?.sessionSequence || '?'}
                            </p>
                        )}
                        </div>

                        <span>
                        {session?.sessionSequence
                            ? `Session ${session.sessionSequence}`
                            : 'Session'}{' '}
                        {session?.sessionName}
                        </span>

                        <span
                        className={`ml-2 rounded-full px-1.5 py-0.5 text-[8px] ${
                            isSelected
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                        >
                        {session?.sessionQNASets?.length}
                        </span>
                    </button>
                    )
                })}
                </div>

                {/* 16b - Selected session Q&A board */}
                {selectedQNASession && (
                <EtUserSessionQNABoard
                    userId={userId}
                    userOrgId={trailBoardUserOrgId}
                    trailId={trailId}
                    sessionId={selectedQNASession.sessionId}
                    sessionQNASets={
                    selectedQNASession.sessionQNASets
                    }
                    responseRole="User"
                    boardTitle={
                    selectedQNASession.sessionSequence
                        ? `Session ${selectedQNASession.sessionSequence} Q&A Board`
                        : 'Session Q&A Board'
                    }
                    cohortId={selectedQNASession.cohortId}
                    cohort={selectedQNASession.cohort}
                />
                )}
            </>
            ) : (
            <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                No Session Q&amp;A boards are available in this trail yet.
                </p>
            </div>
            )}
        </div>
      )}

      {/* 17 - Action Board sections */}
      {activeTrailBoardTab === 'actions' && (
        <div className="rounded bg-white p-3 shadow-sm">
          <p className="text-xs text-gray-500">
            Actions will appear here.
          </p>
        </div>
      )}

    </div>
  )
}

export default EtUserTrailBoardCompProd
